import React from 'react';
import { ChevronDown, ChevronRight } from 'lucide-react';
import type { UseSettingsPageReturn } from '../../hooks/useSettingsPage';
import {
  NOTIFICATION_ROWS,
  NOTIFICATION_ROW_KEYS,
  notificationRowExample,
  notificationSwitchStyles,
  SHEET_SAMPLE_STYLE,
  type NotificationRowKey,
} from '../settings/NotificationSettings';

interface SettingsSheetNotificationsProps {
  expanded: string | null;
  toggle: (section: string) => void;
  s: Pick<UseSettingsPageReturn, 'preferences' | 'handleToggle' | 'handleTimeChange'>;
}

/** The Notifications section of the settings sheet (authed only; the sheet gates it). */
const SettingsSheetNotifications: React.FC<SettingsSheetNotificationsProps> = ({ expanded, toggle, s }) => {
  const prefs = s.preferences;

  const row = (field: NotificationRowKey) => {
    const { label } = NOTIFICATION_ROWS[field];
    const on = Boolean(prefs?.[field]);
    const styles = notificationSwitchStyles(on);
    return (
      <div className="ss-notif-row" key={field}>
        <div>
          <div className="ss-notif-label">{label}</div>
          {/* Secondary ink: `.ss-notif-desc` alone is tertiary at 11px, under AA for these disclosures. */}
          <div className="ss-notif-desc" style={SHEET_SAMPLE_STYLE}>
            {notificationRowExample(NOTIFICATION_ROWS[field])}
          </div>
        </div>
        <button
          type="button"
          role="switch"
          aria-checked={on}
          aria-label={label}
          data-testid={`ss-notif-toggle-${field}`}
          className={`ss-toggle ss-toggle--sm ${on ? 'ss-toggle--on' : ''}`}
          style={styles.track}
          onClick={() => s.handleToggle(field)}
        >
          <div className="ss-toggle-knob" style={styles.knob} />
        </button>
      </div>
    );
  };

  return (
    <div className="ss-section">
      <button className="ss-row ss-row--expandable" onClick={() => toggle('notifications')}>
        <div className="ss-row-left">
          <span className="ss-row-icon">🔔</span>
          <div>
            <div className="ss-row-label">Notifications</div>
            <div className="ss-row-desc">
              {prefs?.morning_briefing_enabled
                ? `Briefing at ${prefs.morning_briefing_time || '8:00'}`
                : 'Briefing off'}
              {prefs?.leave_reminder_enabled ? ', reminders on' : ''}
            </div>
          </div>
        </div>
        {expanded === 'notifications' ? <ChevronDown size={16} /> : <ChevronRight size={16} />}
      </button>

      {expanded === 'notifications' && prefs && (
        <div className="ss-expand-body">
          {NOTIFICATION_ROW_KEYS.map((field) => (
            <React.Fragment key={field}>
              {row(field)}
              {field === 'morning_briefing_enabled' && prefs.morning_briefing_enabled && (
                <div className="ss-notif-row">
                  <div className="ss-notif-label">Briefing Time</div>
                  <input
                    type="time"
                    className="ss-time-input"
                    value={prefs.morning_briefing_time || '08:00'}
                    onChange={(e) => s.handleTimeChange(e.target.value)}
                  />
                </div>
              )}
            </React.Fragment>
          ))}
        </div>
      )}
    </div>
  );
};

export default SettingsSheetNotifications;
